// Currency utilities for displaying prices in user's preferred currency

const CURRENCIES = {
  USD: { symbol: '$', name: 'US Dollar', rate: 1.0 },
  CDF: { symbol: 'FC', name: 'Congolese Franc', rate: 2750.0 },
  EGP: { symbol: '£', name: 'Egyptian Pound', rate: 30.90 },
  ETB: { symbol: 'Br', name: 'Ethiopian Birr', rate: 50.85 },
  GHS: { symbol: '₵', name: 'Ghanaian Cedi', rate: 12.50 },
  GNF: { symbol: 'FG', name: 'Guinean Franc', rate: 8600.0 },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling', rate: 131.00 },
  LRD: { symbol: 'L$', name: 'Liberian Dollar', rate: 190.0 },
  MAD: { symbol: 'd.m.', name: 'Moroccan Dirham', rate: 10.45 },
  MGA: { symbol: 'Ar', name: 'Malagasy Ariary', rate: 4560.0 },
  NGN: { symbol: '₦', name: 'Nigerian Naira', rate: 1550.0 },
  RWF: { symbol: 'FRw', name: 'Rwandan Franc', rate: 1297.50 },
  TZS: { symbol: 'TSh', name: 'Tanzanian Shilling', rate: 2530.0 },
  UGX: { symbol: 'USh', name: 'Ugandan Shilling', rate: 3750.0 },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', rate: 1.36 },
  AUD: { symbol: 'A$', name: 'Australian Dollar', rate: 1.52 },
  JPY: { symbol: '¥', name: 'Japanese Yen', rate: 149.50 },
  CNY: { symbol: '¥', name: 'Chinese Yuan', rate: 7.24 },
  INR: { symbol: '₹', name: 'Indian Rupee', rate: 83.12 },
  BRL: { symbol: 'R$', name: 'Brazilian Real', rate: 4.97 },
  MXN: { symbol: 'Mex$', name: 'Mexican Peso', rate: 17.05 },
  ZAR: { symbol: 'R', name: 'South African Rand', rate: 18.50 },
  XAF: { symbol: 'FCFA', name: 'Central African CFA Franc', rate: 615.90 },
  XOF: { symbol: 'CFA', name: 'West African CFA Franc', rate: 615.90 },
};

/**
 * Dynamically updates the currency rates using live unified data
 * @param {Object} newRatesMap - Map of currency codes to their unified rates
 */
export function updateCurrencyRates(newRatesMap) {
  if (!newRatesMap) return;
  for (const [code, rate] of Object.entries(newRatesMap)) {
    if (CURRENCIES[code] && typeof rate === 'number' && rate > 0) {
      CURRENCIES[code].rate = rate;
    }
  }
}

export const SUPPORTED_ACCOUNT_CURRENCY_CODES = [
  'USD',
  'XAF',
  'XOF',
  'CDF',
  'MGA',
  'GNF',
  'LRD',
  'UGX',
  'ZAR',
  'NGN',
  'KES',
  'GHS',
  'EGP',
  'MAD',
  'RWF',
  'ETB',
  'TZS',
];

export function getCurrencyInfo(currencyCode) {
  const currency = CURRENCIES[currencyCode];
  if (!currency) {
    return null;
  }

  return {
    code: currencyCode,
    symbol: currency.symbol,
    name: currency.name,
  };
}

/**
 * Get currency symbol for a given currency code
 * @param {string} currencyCode - Currency code (e.g., 'USD', 'EUR')
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currencyCode) {
  return CURRENCIES[currencyCode]?.symbol || currencyCode;
}

/**
 * Format amount with currency symbol
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted amount (e.g., '$1,234.56')
 */
export function formatCurrency(amount, currencyCode = 'USD', decimals = 2) {
  const symbol = getCurrencySymbol(currencyCode);
  const numAmount = parseFloat(amount || 0);
  
  // Handle NaN cases
  if (isNaN(numAmount)) {
    return `${symbol}0.${'0'.repeat(decimals)}`;
  }
  
  const formatted = numAmount.toFixed(decimals);
  
  // Format with thousand separators
  const parts = formatted.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decimalPart = parts[1] || '0'.repeat(decimals);
  
  return `${symbol}${integerPart}.${decimalPart}`;
}

/**
 * Format amount with currency symbol (short version, no decimals for integers)
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @returns {string} Formatted amount
 */
export function formatCurrencyShort(amount, currencyCode = 'USD') {
  const symbol = getCurrencySymbol(currencyCode);
  const num = parseFloat(amount || 0);
  
  if (Number.isInteger(num)) {
    return `${symbol}${num.toLocaleString()}`;
  }
  
  return `${symbol}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Get all available currencies
 * @returns {array} Array of currency objects
 */
export function getAvailableCurrencies() {
  return SUPPORTED_ACCOUNT_CURRENCY_CODES
    .map(getCurrencyInfo)
    .filter(Boolean);
}

/**
 * Check if currency code is valid
 * @param {string} currencyCode - Currency code to validate
 * @returns {boolean} True if valid
 */
export function isValidCurrency(currencyCode) {
  return currencyCode in CURRENCIES;
}

/**
 * Convert amount from USD to target currency
 * @param {number} amountInUSD - Amount in USD
 * @param {string} targetCurrency - Target currency code
 * @returns {number} Converted amount
 */
export function convertCurrency(amountInUSD, targetCurrency = 'USD') {
  const numAmount = parseFloat(amountInUSD || 0);
  
  // Handle NaN cases
  if (isNaN(numAmount)) {
    return 0;
  }
  
  if (targetCurrency === 'USD' || !CURRENCIES[targetCurrency]) {
    return numAmount;
  }
  const rate = CURRENCIES[targetCurrency].rate || 1.0;
  return numAmount * rate;
}

/**
 * Convert amount from a source currency back to USD
 * @param {number} amount - Amount in source currency
 * @param {string} sourceCurrency - Source currency code
 * @returns {number} Amount converted to USD
 */
export function convertToUsd(amount, sourceCurrency = 'USD') {
  const numAmount = parseFloat(amount || 0);

  if (isNaN(numAmount)) {
    return 0;
  }

  if (sourceCurrency === 'USD' || !CURRENCIES[sourceCurrency]) {
    return numAmount;
  }

  const rate = CURRENCIES[sourceCurrency].rate || 1.0;
  return numAmount / rate;
}

/**
 * Format currency amount with conversion from USD
 * @param {number} amountInUSD - Amount in USD
 * @param {string} currencyCode - Target currency code
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted amount in target currency
 */
export function formatCurrencyWithConversion(amountInUSD, currencyCode = 'USD', decimals = 2) {
  const convertedAmount = convertCurrency(amountInUSD, currencyCode);
  return formatCurrency(convertedAmount, currencyCode, decimals);
}

/**
 * Format currency amount with conversion from USD (without symbol, with code)
 * @param {number} amountInUSD - Amount in USD
 * @param {string} currencyCode - Target currency code
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted amount (e.g., '1,234.56 USD')
 */
export function formatCurrencyCodeOnly(amountInUSD, currencyCode = 'USD', decimals = 2) {
  const convertedAmount = convertCurrency(amountInUSD, currencyCode);
  const formatted = parseFloat(convertedAmount || 0).toFixed(decimals);
  
  // Format with thousand separators
  const parts = formatted.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decimalPart = parts[1] || '00';
  
  return `${integerPart}.${decimalPart} ${currencyCode}`;
}

